import { UserSubscription, SUBSCRIPTION_PLANS } from './subscription';

// SECURITY: Multi-layer check to prevent production bypass
// Only enable if ALL conditions are met:
// 1. Explicit dev mode flag is 'true' 
// 2. Vite DEV mode is active (development server)
// 3. Environment is NOT production
// 4. Supabase URL is local (localhost/127.0.0.1)
export const LOCAL_DEV_MODE = 
  import.meta.env.VITE_LOCAL_DEV_MODE === 'true' && 
  import.meta.env.DEV && 
  import.meta.env.MODE !== 'production' &&
  (import.meta.env.VITE_SUPABASE_URL?.includes('127.0.0.1') || import.meta.env.VITE_SUPABASE_URL?.includes('localhost'));

export interface LocalSubscriptionOverride {
  enabled: boolean;
  defaultPlan: string;
  bypassPaywall: boolean;
  unlimitedChildren: boolean;
  autoGrantLifetime: boolean;
}

const DEFAULT_LOCAL_CONFIG: LocalSubscriptionOverride = {
  enabled: true,
  defaultPlan: 'family_plan', // Give generous access for development
  bypassPaywall: true,
  unlimitedChildren: true,
  autoGrantLifetime: false // Set to true for lifetime access
};

export class LocalSubscriptionService {
  private static getConfig(): LocalSubscriptionOverride {
    // SECURITY: Double-check that we're in safe development mode
    if (!LOCAL_DEV_MODE) {
      console.error('🚨 SECURITY: Attempted to access local subscription config outside of development mode');
      return { ...DEFAULT_LOCAL_CONFIG, enabled: false, bypassPaywall: false };
    }

    try {
      const localConfig = localStorage.getItem('neurocademy_local_subscription_config');
      if (localConfig) {
        return { ...DEFAULT_LOCAL_CONFIG, ...JSON.parse(localConfig) };
      }
    } catch (error) {
      console.warn('Failed to parse local subscription config:', error);
    }
    return DEFAULT_LOCAL_CONFIG;
  }

  static setConfig(config: Partial<LocalSubscriptionOverride>): void {
    const currentConfig = this.getConfig();
    const newConfig = { ...currentConfig, ...config };
    localStorage.setItem('neurocademy_local_subscription_config', JSON.stringify(newConfig));
    console.log('🔧 Local subscription config updated:', newConfig);
  }

  static isEnabled(): boolean {
    // SECURITY: Triple-check before enabling bypass
    if (!LOCAL_DEV_MODE) {
      return false;
    }
    
    // Additional runtime safety checks
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      console.error('🚨 SECURITY: Local dev mode blocked - not running on localhost');
      return false;
    }
    
    return this.getConfig().enabled;
  }

  static createMockSubscription(userId: string): UserSubscription | null {
    if (!this.isEnabled()) return null;
    
    const config = this.getConfig();
    const planId = config.autoGrantLifetime ? 'lifetime' : config.defaultPlan;
    
    const subscription: UserSubscription = {
      id: `local_dev_${userId}`,
      userId: userId,
      planId: planId,
      status: config.autoGrantLifetime ? 'lifetime' : 'active',
      currentPeriodStart: new Date().toISOString(),
      currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      cancelAtPeriodEnd: false,
      stripeSubscriptionId: `sub_local_dev_${userId}`,
      stripeCustomerId: `cus_local_dev_${userId}`
    };

    console.log('🎭 Created local dev subscription:', subscription);
    return subscription;
  }

  static shouldBypassPaywall(): boolean {
    return this.isEnabled() && this.getConfig().bypassPaywall;
  }

  static getMaxChildrenOverride(): number | null {
    if (!this.isEnabled()) return null;
    
    const config = this.getConfig();
    if (config.unlimitedChildren) return 999;
    
    const plan = SUBSCRIPTION_PLANS.find(p => p.id === config.defaultPlan);
    return plan?.maxChildren || 3;
  }

  static createMockCheckoutUrl(planId: string, interval: 'monthly' | 'yearly'): string {
    const baseUrl = window.location.origin;
    const mockParams = new URLSearchParams({
      mock: 'true',
      plan: planId,
      interval: interval,
      user: 'local_dev'
    });
    
    return `${baseUrl}/subscription/mock-success?${mockParams}`;
  }

  static createMockPortalUrl(): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/subscription/mock-portal`;
  }

  static logSubscriptionAction(action: string, details?: unknown): void {
    if (this.isEnabled()) {
      console.log(`🎭 [LOCAL DEV] ${action}:`, details);
    }
  }
}

// Development helper functions
export const LocalDevHelpers = {
  enableLocalMode: () => {
    LocalSubscriptionService.setConfig({ enabled: true });
  },
  
  disableLocalMode: () => {
    LocalSubscriptionService.setConfig({ enabled: false });
  },
  
  grantLifetime: () => {
    LocalSubscriptionService.setConfig({ 
      autoGrantLifetime: true, 
      defaultPlan: 'lifetime',
      unlimitedChildren: true 
    });
  },
  
  setFreeUser: () => {
    LocalSubscriptionService.setConfig({ 
      enabled: false,
      bypassPaywall: false 
    });
  },
  
  setPlan: (planId: string) => {
    LocalSubscriptionService.setConfig({ defaultPlan: planId });
  },
  
  showConfig: () => {
    console.log('🔧 Current local subscription config:', LocalSubscriptionService.getConfig());
  },
  
  reset: () => {
    localStorage.removeItem('neurocademy_local_subscription_config');
    console.log('🔧 Local subscription config reset to defaults');
  }
};

// SECURITY: Only expose dev helpers in development builds with strict checks
if (import.meta.env.DEV && LOCAL_DEV_MODE) {
  // Additional production check - ensure we're not in a production build
  if (import.meta.env.MODE === 'production') {
    console.error('🚨 SECURITY: Dev helpers blocked in production mode');
  } else {
    (window as typeof window & { neurocademyDev: typeof LocalDevHelpers }).neurocademyDev = LocalDevHelpers;
    console.log('🎭 Local dev mode enabled! Use window.neurocademyDev for subscription testing');
    console.log('Available commands:');
    console.log('- neurocademyDev.showConfig()');
    console.log('- neurocademyDev.grantLifetime()'); 
    console.log('- neurocademyDev.setFreeUser()');
    console.log('- neurocademyDev.setPlan("family_plan")');
    console.log('- neurocademyDev.reset()');
  }
}